import { cache } from './chunk-UL2DPD2B.js';

// ../base/api/error.ts
var JwApiError = class extends Error {
  response;
  constructor(response) {
    super("JW_API_ERROR");
    this.response = response;
  }
};

// ../base/fetch/index.ts
function responseToPlainObj(response) {
  const { headers, ok, redirected, status, statusText, type, url } = response;
  return {
    // Headers to plain object
    headers: Array.from(headers.entries()).reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {}),
    ok,
    redirected,
    status,
    statusText,
    type,
    url
  };
}
async function parseResponse(response) {
  const plainResponse = responseToPlainObj(response);
  let json;
  try {
    if (response.status !== 204) {
      json = await response.json();
    }
  } catch (e) {
  }
  const validHttpStatus = response.status >= 200 && response.status < 300;
  if (response.ok && validHttpStatus) {
    return {
      ...plainResponse,
      json
    };
  } else {
    throw new JwApiError({
      ...plainResponse,
      json
    });
  }
}
function parseHeaders(headers, {
  authToken,
  contentType
}) {
  const ret = Object.entries(headers ?? {}).reduce((acc, [key, value]) => {
    if (key && typeof value !== "undefined") {
      acc[key] = String(value);
    }
    return acc;
  }, {});
  if (authToken) {
    ret["Authorization"] = `${authToken}`;
  }
  if (ret["Content-Type"] !== null) {
    switch (contentType) {
      case "json":
        ret["Content-Type"] = "application/json";
        break;
      case "urlencoded":
        ret["Content-Type"] = "application/x-www-form-urlencoded";
    }
  }
  return ret;
}
function objToUrlSearchParams(element, key = "", usp = new URLSearchParams()) {
  if (element && typeof element === "object") {
    for (const idx in element) {
      objToUrlSearchParams(element[idx], key ? key + "[" + idx + "]" : idx, usp);
    }
  } else {
    usp.set(key, element);
  }
  return usp;
}
async function jsonFetch(url, fetchOpts, customOpts) {
  const inputType = customOpts?.inputType ?? (typeof fetchOpts.body === "object" ? "json" : void 0);
  let body;
  if (typeof fetchOpts.body !== "undefined") {
    switch (inputType) {
      case "json":
        body = JSON.stringify(fetchOpts.body);
        break;
      case "urlencoded":
        body = objToUrlSearchParams(fetchOpts.body);
        break;
      default:
        body = String(fetchOpts.body);
        break;
    }
  }
  const headers = parseHeaders(fetchOpts?.headers, {
    authToken: customOpts?.authToken,
    contentType: inputType
  });
  customOpts?.debug && console.debug(url);
  const req = {
    ...fetchOpts,
    headers,
    body
  };
  customOpts?.debug && console.debug(req);
  const response = await fetch(url, req);
  const fetchResponse = await parseResponse(response);
  customOpts?.debug && console.debug(JSON.stringify(fetchResponse, null, 2));
  return fetchResponse;
}

// ../base/fetch/util.ts
function parseQueryParams(usp, params) {
  usp ??= new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    usp?.set(key, String(value));
  });
  return usp;
}

// ../base/util/url.ts
function joinParts(...parts) {
  return parts.filter(Boolean).join("/").split("/").filter(Boolean).join("/");
}
function joinUrl(...parts) {
  if (parts[0]?.startsWith("http")) {
    const url = new URL(parts.join("/"));
    url.pathname = "/" + joinParts(...url.pathname.split("/"));
    return url.toString();
  }
  return joinParts(...parts);
}

// ../base/api/list-operation/index.ts
function constructListQuery(q, i = 0) {
  const strings = q.children.map((child) => {
    if (typeof child === "object") {
      return constructListQuery(child, i + 1);
    } else {
      return child?.trim();
    }
  }).filter(Boolean);
  if (!strings.length) {
    return "";
  }
  const inner = strings.join(` ${q.operator} `).trim();
  return i === 0 ? inner : `( ${inner} )`;
}
function constructSortString(sort) {
  return sort?.map((s) => {
    if (s && typeof s === "object" && "field" in s && "order" in s && s.field && s.order) {
      return `${s.field}:${s.order}`;
    }
  }).filter(Boolean).join(",");
}
function parseListParams(usp, params) {
  usp ??= new URLSearchParams();
  if (params?.page) {
    usp.set("page", String(params.page));
  }
  if (params?.page_length) {
    usp.set("page_length", String(params.page_length));
  }
  if (params?.sort && Array.isArray(params.sort)) {
    usp.delete("sort");
    const str = constructSortString(params.sort);
    if (str) {
      usp.set("sort", str);
    }
  }
  if (params?.q) {
    usp.delete("q");
    const str = typeof params.q === "string" ? params.q.trim() : constructListQuery(params.q);
    if (str) {
      usp.set("q", str);
    }
  }
  return usp;
}

// ../base/api/index.ts
var parseResourcePath = (endpoint, routeParams, queryParams) => {
  let ret = endpoint;
  if (routeParams) {
    Object.entries(routeParams).forEach(([key, value]) => {
      ret = ret.replace(`:${key}`, String(value) ?? "");
    });
  }
  const matches = ret.match(/:([\w\d]+)/gim);
  if (matches !== null) {
    throw new Error(`Missing route param(s): ${matches.map((m) => m.substring(1))}`);
  }
  if (queryParams) {
    let usp = new URLSearchParams();
    usp = parseQueryParams(usp, queryParams);
    usp = parseListParams(usp, queryParams);
    const queryString = usp.toString();
    if (queryString) {
      ret += `?${queryString}`;
    }
  }
  return ret;
};
var parseInput = async (input, zodObject, transformFn) => {
  let ret;
  if (zodObject) {
    const parsed = zodObject.safeParse(input);
    if ("error" in parsed) {
      throw new Error(parsed.error.toString());
    }
    ret = parsed.data;
  }
  if (transformFn) {
    ret = await transformFn(ret);
  }
  return ret;
};
async function request({ baseUrl, operations }, action, params, authToken, opts) {
  const operation = operations[action];
  let { input, headers, routeParams, queryParams } = params;
  if (operation.transform?.routeParams && routeParams) {
    routeParams = operation.transform.routeParams(routeParams);
  }
  input = await parseInput(input, operation.input, operation.transform?.input);
  headers = await parseInput(headers, operation.headers, operation.transform?.headers);
  queryParams = await parseInput(queryParams, operation.queryParams);
  const parsedPath = parseResourcePath(operation.path, routeParams, queryParams);
  const fullUrl = joinUrl(baseUrl, parsedPath);
  const { debug, ...restOpts } = opts ?? {};
  try {
    const res = await jsonFetch(
      fullUrl,
      {
        ...restOpts,
        method: operation.method,
        body: input,
        headers: {
          ...headers,
          // Ensure we keep any headers passed in the generic fetch options
          ...restOpts?.headers
        }
      },
      {
        authToken,
        debug,
        inputType: operation.inputType
      }
    );
    if (operation.transform?.output) {
      res.json = await operation.transform.output(res.json, {
        routeParams,
        queryParams
      });
      opts?.debug && console.debug("transformed", res.json);
    }
    operation.events?.onSuccess?.(res);
    if (opts && "__test__" in opts && opts?.__test__) {
      res.__operation = operation;
    }
    return res;
  } catch (err) {
    operation.events?.onError?.(
      // TS can't determine the correct output type here
      err
    );
    throw err;
  }
}

// src/api/index.ts
async function jwApi(service, action, params, opts) {
  const authToken = cache.getToken();
  const operations = (await (await import('./conf-JSTNCQSR.js')).default[service]())["default"];
  return request(operations, action, params, authToken, opts);
}

export { JwApiError, jwApi };
